import { Controller } from "@hotwired/stimulus"
import { MISMATCH, PADDED, SWAP, aspectPromptCase, flatSlot } from "@/lib/aspectPrompt"

const CHECKED_SLOTS = ["primary", "preview"]

export default class extends Controller {
  static targets = [
    "photosSlot",
    "sizeSlot",
    "width",
    "height",
    "frame",
    "swapTemplate",
    "swappedTemplate",
    "paddedTemplate",
    "mismatchTemplate",
  ]
  static values = { photos: Object, tolerances: Object, autosaveUrl: String }
  static classes = ["highlight"]

  connect() {
    this.show("photos")
  }

  photoUploaded({ detail: { slot, width, height, imageId } }) {
    if (!CHECKED_SLOTS.includes(slot)) return

    this.photosValue = { ...this.photosValue, [slot]: { width, height, image_id: imageId } }
    this.show("photos")
  }

  photoRemoved({ detail: { slot } }) {
    if (!CHECKED_SLOTS.includes(slot)) return

    const { [slot]: _removed, ...photos } = this.photosValue
    this.photosValue = photos
    this.show("photos")
  }

  sizeChanged() {
    this.show("size")
  }

  tileMounted({ detail: { slot } }) {
    if (slot === this.highlightedSlot) this.highlight(slot)
  }

  async swap(event) {
    event.preventDefault()
    if (this.saving) return

    this.track("aspect_prompt_accepted", { action: "swap" })
    const original = { width: this.width, height: this.height }
    const saved = await this.saveSize({ width: original.height, height: original.width })
    if (!saved) return

    this.swappedFrom = original
    this.render()
  }

  async undo(event) {
    event.preventDefault()
    if (this.saving) return

    this.track("aspect_prompt_undone")
    const saved = await this.saveSize(this.swappedFrom)
    if (!saved) return

    this.swappedFrom = null
    this.render()
  }

  replacePhoto(event) {
    event.preventDefault()
    this.track("aspect_prompt_accepted", { action: "replace" })
    this.element.querySelector(`[role="photo-upload-input-${this.slot}"]`)?.click()
  }

  editMeasurements(event) {
    event.preventDefault()
    this.track("aspect_prompt_accepted", { action: "edit" })
    this.widthTarget.scrollIntoView({ behavior: "smooth", block: "center" })
    this.widthTarget.focus({ preventScroll: true })
  }

  keep(event) {
    event.preventDefault()
    this.track("aspect_prompt_dismissed")
    writeStorage(this.combinationKey, "1")
    this.render()
  }

  show(location) {
    this.location = location
    this.swappedFrom = null
    this.render()
  }

  render() {
    this.clear()

    if (this.swappedFrom) {
      this.append(this.swappedTemplateTarget)
      return
    }

    this.prompt = this.currentPrompt()
    if (!this.prompt) return

    this.append(this.templateFor(this.prompt))
    this.trackShown()
  }

  currentPrompt() {
    if (readStorage(this.combinationKey)) return null

    return aspectPromptCase({
      photo: this.photo,
      width: this.width,
      height: this.height,
      framed: this.framed,
      tolerances: this.tolerancesValue,
    })
  }

  templateFor(prompt) {
    return {
      [SWAP]: this.swapTemplateTarget,
      [PADDED]: this.paddedTemplateTarget,
      [MISMATCH]: this.mismatchTemplateTarget,
    }[prompt.case]
  }

  append(template) {
    this.highlight(this.slot)
    this.slotFor(this.location).append(this.message(template))
  }

  trackShown() {
    this.shownCombinations ??= new Set()
    if (this.shownCombinations.has(this.combinationKey)) return

    this.shownCombinations.add(this.combinationKey)
    this.track("aspect_prompt_shown")
  }

  track(event, properties = {}) {
    window.posthog?.capture(event, {
      listing_id: Number(this.element.dataset.listingId),
      case: this.prompt.case,
      off_pct: this.prompt.offPct,
      location: this.location,
      slot: this.slot,
      ...properties,
    })
  }

  clear() {
    this.photosSlotTarget.replaceChildren()
    this.sizeSlotTarget.replaceChildren()
    CHECKED_SLOTS.forEach((slot) => this.tileFor(slot)?.classList.remove(...this.highlightClasses))
    this.highlightedSlot = null
  }

  highlight(slot) {
    this.highlightedSlot = slot
    this.tileFor(slot)?.classList.add(...this.highlightClasses)
  }

  message(template) {
    const message = template.content.firstElementChild.cloneNode(true)
    const orientation = this.photo.height > this.photo.width ? "portrait" : "landscape"

    message.querySelectorAll("[data-aspect-prompt-field=width]").forEach((field) => (field.textContent = this.width))
    message.querySelectorAll("[data-aspect-prompt-field=height]").forEach((field) => (field.textContent = this.height))
    message
      .querySelectorAll(`[data-aspect-prompt-orientation]:not([data-aspect-prompt-orientation=${orientation}])`)
      .forEach((sentence) => sentence.remove())
    if (this.location === "size") message.querySelector("[data-aspect-prompt-role=edit-measurements]")?.remove()

    return message
  }

  async saveSize({ width, height }) {
    this.saving = true

    try {
      const response = await fetch(this.autosaveUrlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content ?? "",
        },
        body: JSON.stringify({ listing: { width, height } }),
      })
      if (!response.ok) return false

      this.widthTarget.value = width
      this.heightTarget.value = height
      return true
    } catch {
      return false
    } finally {
      this.saving = false
    }
  }

  slotFor(location) {
    return location === "size" ? this.sizeSlotTarget : this.photosSlotTarget
  }

  tileFor(slot) {
    return this.element.querySelector(`[role="photo-upload-area-${slot}"]`)
  }

  get slot() {
    return flatSlot(this.photosValue)
  }

  get photo() {
    return this.photosValue[this.slot]
  }

  get combinationKey() {
    return `aspect-prompt:${this.element.dataset.listingId}:${this.photo?.image_id}:${this.width}x${this.height}`
  }

  get width() {
    return Number(this.widthTarget.value)
  }

  get height() {
    return Number(this.heightTarget.value)
  }

  get framed() {
    return this.frameTarget.value === "true"
  }
}

function readStorage(key) {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function writeStorage(key, value) {
  try {
    localStorage.setItem(key, value)
  } catch {}
}
